import { useStore as useZustandStore } from 'zustand'
import { createStore, type StateCreator, type StoreApi } from 'zustand/vanilla'

/**
 * Client state that is shared across every mounted component.
 *
 * Independent React roots can't share Context, so any state that more than one
 * widget cares about has to live outside React entirely. A zustand vanilla
 * store at module scope does that: a nav widget writing to it re-renders an
 * account widget elsewhere on the page with no coordination between them.
 *
 * Usage:
 *
 *   const session = createSharedStore<SessionState>((set) => ({
 *     user: null,
 *     signOut: () => set({ user: null }),
 *   }))
 *
 *   export const useSession = session.useStore
 *   // ...and outside React (event handlers, page code):
 *   session.get().user
 */
export interface SharedStore<T> {
  /** The underlying store, for use outside React. */
  store: StoreApi<T>
  /** Hook: subscribe a component to a slice of this store. */
  useStore: <U>(selector: (state: T) => U) => U
  get: StoreApi<T>['getState']
  set: StoreApi<T>['setState']
  subscribe: StoreApi<T>['subscribe']
}

export function createSharedStore<T>(initializer: StateCreator<T, [], []>): SharedStore<T> {
  const store = createStore<T>()(initializer)

  function useBoundStore<U>(selector: (state: T) => U): U {
    return useZustandStore(store, selector)
  }

  return {
    store,
    useStore: useBoundStore,
    get: store.getState,
    set: store.setState,
    subscribe: store.subscribe,
  }
}
